import React from 'react';
import cx from 'classnames';
import { Modal as CrateModal } from '@tcc/crate';

import SelectOperationModal from '../OperationNotAllowed';
import { EnhancedScoutingForm } from '../../../containers/layers/scouting/EnhancedScoutingForm';
import { RegionNoteForm, RegionReport } from '../../../components/FieldRegionReport';
import { PaywallModal } from '../PaywallModal/PaywallModal';
import { ScoutingForm } from '../../../containers/layers/scouting';
import { ModalType } from '../../../types';

import { useAppDispatch, useAppSelector } from '../../../utils';
import { getModalShow, getModalSize, getModalType, getModalMode } from '../../../selectors/modalSelectors';
import { closeModal } from '../../../actions/modalActions';

import styles from './Modal.css';
import SilageModal from '../SilageModal/SilageModal';

export type ModalProps = {
  index: number;
  modal?: React.ReactElement<{ index?: number }>;
  className?: string;
};

const getClassName = (modalType?: ModalType): string => {
  if (modalType === ModalType.REGION_REPORT || modalType === ModalType.REGION_NOTE) {
    return styles.regionReportModal;
  }
  if (modalType === ModalType.ENHANCED_SCOUTING_FORM) {
    return styles.enhancedScoutingModal;
  }
  if (modalType === ModalType.SCOUTING_FORM) {
    return styles.scoutingModal;
  }

  return '';
};

export interface DialogBaseProps {
  index: number;
}

type MigratedModalType = ModalType.REGION_REPORT | ModalType.REGION_NOTE;

const dfsModals: Record<MigratedModalType, React.ComponentType<DialogBaseProps>> = {
  [ModalType.REGION_REPORT]: RegionReport,
  [ModalType.REGION_NOTE]: RegionNoteForm,
};

export const Modal = ({ modal, index, className }: ModalProps) => {
  const dispatch = useAppDispatch();
  const show = useAppSelector(state => getModalShow(state, { index }));
  const modalType = useAppSelector(getModalType);
  const modalMode = useAppSelector(getModalMode);

  // @deprecated
  const size = useAppSelector(getModalSize);
  const featureFlags = useAppSelector(state => state.feature.userConfigFeatures);
  const modalClassName = getClassName(modalType);

  const isNullModal = modalType === undefined;
  const isScoutingModal = modalType === ModalType.SCOUTING_FORM;
  const isOperationNotAllowedModal = modalType === ModalType.OPERATION_NOT_ALLOWED;
  const isEnhancedScoutingModal = modalType === ModalType.ENHANCED_SCOUTING_FORM;
  const isPaywall = modalType === ModalType.PAYWALL;
  const isSilageModal = modalType === ModalType.SILAGE;

  if (isPaywall) {
    return (
      <PaywallModal
        show={show}
        modalMode={modalMode}
        featureFlags={featureFlags}
        onClose={() => {
          dispatch(closeModal(index));
        }}
      />
    );
  }

  if (modalType && dfsModals[modalType]) {
    if (!show) {
      return null;
    }

    const Component = dfsModals[modalType];
    return <Component index={index} />;
  }

  return (
    <CrateModal
      className={cx(className, modalClassName)}
      show={show}
      size={size}
    >
      {isNullModal && modal ? React.cloneElement(modal, { index }) : null}
      {isScoutingModal && <ScoutingForm index={index} />}
      {isOperationNotAllowedModal && <SelectOperationModal index={index} />}
      {isEnhancedScoutingModal && <EnhancedScoutingForm index={index} />}
      {isSilageModal && (
        <SilageModal
          index={index}
          onClose={() => {
            dispatch(closeModal(index));
          }}
        />
      )}
    </CrateModal>
  );
};
